import { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2, Clock, ExternalLink, Loader2, ShieldCheck, XCircle, Camera,
} from "lucide-react";
import { api, getErrorMessage } from "@/lib/api";
import LiveSelfieCapture from "@/components/kyc/LiveSelfieCapture";

const TRUID_STATUSES = {
  digilocker_pending: "Complete DigiLocker authorization in the browser window.",
  digilocker_failed: "DigiLocker verification failed. Try again.",
  awaiting_pan: "Enter your PAN for verification (PAN Detailed).",
  pan_verify_failed: "PAN verification failed. Check the number and retry.",
  awaiting_selfie: "Take a live selfie for face verification.",
  face_match_failed: "Face match failed. Retake your live selfie and retry.",
  approved: "Identity verified via TruID DigiLocker + PAN + Face Match.",
};

function Banner({ kycStatus, truidStatus, reason, digilockerReason }) {
  if (kycStatus === "approved") {
    return (
      <div className="flex items-start gap-3 rounded-xl border px-5 py-4 bg-up/10 text-up border-up/30">
        <CheckCircle2 size={18} className="mt-0.5" />
        <div className="text-[14px] font-medium">KYC approved — you can start trading.</div>
      </div>
    );
  }
  if (kycStatus === "pending_review") {
    return (
      <div className="flex items-start gap-3 rounded-xl border px-5 py-4 bg-warning/10 text-warning border-warning/30">
        <Clock size={18} className="mt-0.5" />
        <div className="text-[14px] font-medium">KYC under manual review.</div>
      </div>
    );
  }
  if (kycStatus === "rejected") {
    return (
      <div className="flex items-start gap-3 rounded-xl border px-5 py-4 bg-down/10 text-down border-down/30">
        <XCircle size={18} className="mt-0.5" />
        <div className="text-[14px] font-medium">KYC rejected: {reason || "please try again"}</div>
      </div>
    );
  }
  const hint =
    (truidStatus === "digilocker_failed" && digilockerReason) ||
    TRUID_STATUSES[truidStatus] ||
    "Step 1: DigiLocker Aadhaar → Step 2: PAN Detailed → Step 3: Face match.";
  return (
    <div className="flex items-start gap-3 rounded-xl border px-5 py-4 bg-primary/10 text-primary border-primary/30">
      <ShieldCheck size={18} className="mt-0.5" />
      <div className="text-[14px] font-medium">{hint}</div>
    </div>
  );
}

function Step({ n, label, active, done }) {
  return (
    <div
      className={`flex items-center gap-2 text-[13px] ${
        done ? "text-up" : active ? "text-primary" : "text-muted-foreground"
      }`}
    >
      <span
        className={`w-6 h-6 rounded-full grid place-items-center text-[11px] font-semibold border ${
          done
            ? "bg-up/15 border-up/40"
            : active
              ? "bg-primary/15 border-primary/40"
              : "border-border"
        }`}
      >
        {done ? "✓" : n}
      </span>
      {label}
    </div>
  );
}

export default function TruidKycFlow() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState(null);
  const [status, setStatus] = useState(null);
  const [pan, setPan] = useState("");
  const [selfieFile, setSelfieFile] = useState(null);

  const load = useCallback(async () => {
    setErr("");
    try {
      const [{ data: m }, { data: s }] = await Promise.all([
        api.get("/api/kyc/mode"),
        api.get("/api/kyc/truid/status"),
      ]);
      setMode(m);
      setStatus(s);
      const existing = s?.pan_info?.number || s?.truid?.pan?.pan_number || "";
      if (existing) setPan(String(existing).toUpperCase());
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    function onMessage(ev) {
      if (ev.origin !== window.location.origin) return;
      if (ev.data?.type === "truid-digilocker-complete") {
        void load();
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [load]);

  async function startDigiLocker() {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/api/kyc/truid/digilocker/init", { client: "web" });
      const url = data?.url;
      if (!url) {
        setErr("DigiLocker did not return a sign-in link. Please try again.");
        setBusy(false);
        return;
      }
      // Same tab. A popup is severed by Cross-Origin-Opener-Policy and drops
      // the callback (code / codeVerifier / state) on the way back.
      window.location.assign(url);
    } catch (e) {
      setErr(getErrorMessage(e));
      setBusy(false);
    }
  }

  async function submitPan() {
    const value = pan.trim().toUpperCase();
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value)) {
      setErr("Enter a valid 10-character PAN (e.g. ABCDE1234F).");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post("/api/kyc/truid/pan/verify", { pan: value });
      await load();
    } catch (e) {
      setErr(getErrorMessage(e));
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function submitSelfie() {
    if (!selfieFile) {
      setErr("Capture a live selfie with your camera first.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", selfieFile);
      await api.post("/api/kyc/truid/face-match", fd);
      setSelfieFile(null);
      await load();
    } catch (e) {
      setErr(getErrorMessage(e));
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function restart() {
    setBusy(true);
    setErr("");
    try {
      await api.post("/api/kyc/truid/restart");
      setPan("");
      setSelfieFile(null);
      await load();
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div className="skeleton h-64 w-full" />;
  }

  const kycStatus = status?.kyc_status;
  const truidStatus = status?.truid_status;
  const approved = kycStatus === "approved" || truidStatus === "approved";
  const needDigilocker =
    !approved &&
    (!truidStatus ||
      truidStatus === "digilocker_pending" ||
      truidStatus === "digilocker_failed");
  const needPan =
    !approved && (truidStatus === "awaiting_pan" || truidStatus === "pan_verify_failed");
  const needSelfie =
    !approved && (truidStatus === "awaiting_selfie" || truidStatus === "face_match_failed");

  const step1Done =
    approved ||
    ["awaiting_pan", "pan_verify_failed", "awaiting_selfie", "face_match_failed", "approved"].includes(
      truidStatus
    );
  const step2Done =
    approved || ["awaiting_selfie", "face_match_failed", "approved"].includes(truidStatus);
  const step3Done = approved;

  if (!mode?.truid_configured) {
    return (
      <div className="rounded-xl border border-border bg-surface/50 p-5 text-sm text-muted-foreground">
        TruID auto KYC is not configured. Ask admin to set <code>TRUID_API_ID</code> and{" "}
        <code>TRUID_API_SECRET</code> in the backend environment, then enable{" "}
        <strong>Auto KYC</strong> in Admin → KYC Settings.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Banner
        kycStatus={kycStatus}
        truidStatus={truidStatus}
        reason={status?.rejection_reason}
        digilockerReason={status?.digilocker_failure_reason}
      />

      <div className="flex flex-wrap gap-4 px-1">
        <Step n={1} label="DigiLocker Aadhaar" active={needDigilocker} done={step1Done} />
        <Step n={2} label="PAN Detailed" active={needPan} done={step2Done} />
        <Step n={3} label="Face match" active={needSelfie} done={step3Done} />
      </div>

      {err ? (
        <div className="rounded-xl px-4 py-3 text-sm text-down bg-down/10 border border-down/25">{err}</div>
      ) : null}

      {status?.truid?.aadhaar?.name || status?.full_name ? (
        <div className="rounded-xl border border-border bg-surface/40 px-4 py-3 text-[13px] space-y-1">
          <div className="text-muted-foreground text-[11px] uppercase tracking-wider font-semibold">
            Verified identity
          </div>
          <div className="font-medium text-foreground">
            {status?.full_name || status?.truid?.aadhaar?.name}
          </div>
          {status?.truid?.aadhaar?.aadhaar_number ? (
            <div className="text-muted-foreground">Aadhaar · {status.truid.aadhaar.aadhaar_number}</div>
          ) : null}
          {status?.pan_info?.number || status?.truid?.pan?.pan_number ? (
            <div className="text-muted-foreground">
              PAN · {status?.pan_info?.number || status?.truid?.pan?.pan_number}
              {status?.pan_info?.name || status?.truid?.pan?.name
                ? ` · ${status?.pan_info?.name || status?.truid?.pan?.name}`
                : ""}
            </div>
          ) : null}
        </div>
      ) : null}

      {status?.pan_verify && !status.pan_verify.verified ? (
        <div className="rounded-xl border border-down/30 bg-down/10 text-down px-4 py-3 text-[13px]">
          PAN: {status.pan_verify.message || "Verification failed"}
        </div>
      ) : null}

      {status?.face_match ? (
        <div
          className={`rounded-xl border px-4 py-3 text-[13px] ${
            status.face_match.verified
              ? "border-up/30 bg-up/10 text-up"
              : "border-down/30 bg-down/10 text-down"
          }`}
        >
          Face match: {status.face_match.verified ? "Passed" : "Failed"}
          {status.face_match.match_percentage != null
            ? ` · ${Number(status.face_match.match_percentage).toFixed(0)}%`
            : ""}
          {status.face_match.message ? ` — ${status.face_match.message}` : ""}
        </div>
      ) : null}

      {!approved && needDigilocker ? (
        <div className="rounded-xl border border-border bg-surface/30 p-5 space-y-3">
          <div className="text-[14px] font-medium text-foreground">Step 1 — DigiLocker Aadhaar</div>
          <p className="text-[13px] text-muted-foreground leading-relaxed">
            Sign in with Aadhaar OTP on DigiLocker. We fetch your e-KYC (name, DOB, address, photo).
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={startDigiLocker}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-background bg-primary disabled:opacity-40"
            data-testid="truid-digilocker-start"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ExternalLink size={16} />}
            {truidStatus === "digilocker_pending" ? "Re-open DigiLocker" : "Verify with DigiLocker"}
          </button>
        </div>
      ) : null}

      {!approved && needPan ? (
        <div className="rounded-xl border border-border bg-surface/30 p-5 space-y-3">
          <div className="text-[14px] font-medium text-foreground">Step 2 — PAN Detailed</div>
          <p className="text-[13px] text-muted-foreground leading-relaxed">
            Enter your PAN. We verify it against NSDL and match the name to your DigiLocker Aadhaar.
          </p>
          <input
            type="text"
            value={pan}
            maxLength={10}
            placeholder="ABCDE1234F"
            onChange={(e) => setPan(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
            className="w-full max-w-xs rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-mono tracking-wider uppercase"
            data-testid="truid-pan-input"
          />
          <button
            type="button"
            disabled={busy || pan.length !== 10}
            onClick={submitPan}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-background bg-primary disabled:opacity-40"
            data-testid="truid-pan-submit"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            Verify PAN
          </button>
        </div>
      ) : null}

      {!approved && needSelfie ? (
        <div className="rounded-xl border border-border bg-surface/30 p-5 space-y-3">
          <div className="text-[14px] font-medium text-foreground flex items-center gap-2">
            <Camera size={16} /> Step 3 — Live selfie & face match
          </div>
          <p className="text-[13px] text-muted-foreground leading-relaxed">
            Use your camera for a live selfie. We match it against your DigiLocker Aadhaar photo.
            Uploading a saved photo is not allowed.
          </p>
          <LiveSelfieCapture
            busy={busy}
            testIdPrefix="truid-selfie"
            onCapture={(file) => {
              setSelfieFile(file || null);
              setErr("");
            }}
          />
          <button
            type="button"
            disabled={busy || !selfieFile}
            onClick={submitSelfie}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-background bg-primary disabled:opacity-40"
            data-testid="truid-face-match-submit"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            Submit face match
          </button>
        </div>
      ) : null}

      {!approved ? (
        <button
          type="button"
          disabled={busy}
          onClick={restart}
          className="text-[12px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
        >
          Restart DigiLocker KYC
        </button>
      ) : null}
    </div>
  );
}
